import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Check, Crown, Users, Building2, Loader2, Shield } from "lucide-react";
import { KLARTEXT_PLANS, KlartextPlan } from "@/lib/klartext-plans";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";

interface UpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLoginClick?: () => void;
  isAuthenticated?: boolean;
}

export function UpgradeModal({ open, onOpenChange, onLoginClick, isAuthenticated = false }: UpgradeModalProps) {
  const [loadingPlan, setLoadingPlan] = useState<KlartextPlan | null>(null);
  const { toast } = useToast();

  const handleSelectPlan = async (planId: KlartextPlan) => {
    if (planId === 'free') return;
    
    if (!isAuthenticated) {
      onLoginClick?.();
      onOpenChange(false);
      return;
    }

    setLoadingPlan(planId);
    
    try {
      const { data, error } = await supabase.functions.invoke('klartext-create-checkout', {
        body: { plan: planId },
      });

      if (error) throw new Error(error.message);
      if (data.error) throw new Error(data.error);
      
      if (data.url) {
        window.open(data.url, '_blank');
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      toast({
        title: 'Fehler',
        description: error.message || 'Checkout konnte nicht gestartet werden.',
        variant: 'destructive',
      });
    } finally {
      setLoadingPlan(null);
    }
  };

  const planIcons: Record<KlartextPlan, React.ReactNode> = {
    free: <Shield className="h-6 w-6" />,
    pro: <Crown className="h-6 w-6" />,
    familie: <Users className="h-6 w-6" />,
    firma: <Building2 className="h-6 w-6" />,
  };

  const plans = Object.values(KLARTEXT_PLANS).filter(p => p.id !== 'free');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            Unbegrenzte Sicherheit
          </DialogTitle>
          <DialogDescription className="text-center text-base">
            Schützen Sie sich und Ihre Familie vor Online-Betrug – ohne Limits.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 mt-6">
          {plans.map((plan) => (
            <Card 
              key={plan.id}
              className={`relative overflow-hidden transition-all hover:shadow-lg ${
                plan.popular ? 'ring-2 ring-primary shadow-lg' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 right-0 bg-primary text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                  BELIEBT
                </div>
              )}
              
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      plan.id === 'pro' ? 'bg-yellow-100 text-yellow-600' :
                      plan.id === 'familie' ? 'bg-blue-100 text-blue-600' :
                      'bg-purple-100 text-purple-600'
                    }`}>
                      {planIcons[plan.id]}
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-slate-900">{plan.name}</h3>
                      <p className="text-2xl font-black text-primary">{plan.priceFormatted}</p>
                    </div>
                  </div>
                  
                  <Button
                    onClick={() => handleSelectPlan(plan.id)}
                    disabled={loadingPlan !== null}
                    className={`rounded-xl font-semibold ${
                      plan.popular 
                        ? 'bg-gradient-to-r from-primary to-blue-600 text-white' 
                        : 'bg-slate-100 text-slate-800 hover:bg-slate-200'
                    }`}
                  >
                    {loadingPlan === plan.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : isAuthenticated ? (
                      'Auswählen'
                    ) : (
                      'Anmelden'
                    )}
                  </Button>
                </div>
                
                <ul className="mt-4 space-y-2">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        <p className="text-xs text-slate-500 text-center mt-4">
          Sicher bezahlen mit Stripe. Jederzeit kündbar.
        </p>
      </DialogContent>
    </Dialog>
  );
}
